<template>
  <div class="home vertical-centered h-full">
    <!-- if the request to the api is still pending -->
    <div v-if="waitingForResponse && validQueryParams">
      <i18n-t keypath="views.thirdPartyResult.loading" tag="h1">
        <template #toolName>
          <span class="highlight">{{ toolName }}</span>
        </template>
      </i18n-t>
      <div class="row" style="margin-top: 2rem">
        <img
          :alt="i18n.t('notification.loading')"
          src="https://logos.saasmetrix.io/loading.gif"
          class="integration-icon" />
      </div>
    </div>

    <!-- if the query parameters are valid, and the api call succeeds -->
    <div v-if="validQueryParams && apiSuccess && !waitingForResponse">
      <i18n-t keypath="views.thirdPartyResult.success" tag="h1">
        <template #toolName>
          <span class="highlight">{{ toolName }}</span>
        </template>
      </i18n-t>
      <div class="row" style="margin-top: 2rem">
        <BadgeCheck :size="30" />
      </div>
    </div>

    <!-- if the query parameters are valid, but the api call leads to an error -->
    <div v-if="validQueryParams && !apiSuccess && !waitingForResponse">
      <el-result icon="error" style="margin-bottom: -3rem"> </el-result>

      <i18n-t keypath="views.thirdPartyResult.error.title" tag="h1">
        <template #toolName>
          <span class="highlight">{{ toolName }}</span>
        </template>
        <template #noSuccess>
          <span class="error">
            {{ i18n.t('views.thirdPartyResult.error.noSuccess') }}</span
          >
        </template>
      </i18n-t>
      <h2>{{ i18n.t('views.thirdPartyResult.error.subTitle') }}</h2>
    </div>

    <!-- if the query parameters are not valid -->
    <div v-if="!waitingForResponse && !validQueryParams">
      <el-result icon="error" style="margin-bottom: -3rem"> </el-result>
      <i18n-t keypath="views.thirdPartyResult.noValidQuery.title" tag="h1">
        <template #break>
          <br />
          <span>{{ i18n.t('views.thirdPartyResult.noValidQuery.or') }}</span>
        </template>
        <template #support>
          <el-link
            href="mailto:bug@saasmetrix.io"
            style="font-size: inherit; margin-bottom: 0.3rem"
            type="danger">
            {{ i18n.t('views.thirdPartyResult.noValidQuery.support') }}
          </el-link>
        </template>
      </i18n-t>
    </div>

    <!-- a notice to close the window (if query params are valid, it gets closed automatically)
    the style margin-top=2rem does only need to be there, if the api call succeeds (it would look ugly on the other templates)-->
    <div :style="{ 'margin-top': apiSuccess ? '2rem' : '0' }">
      <i18n-t keypath="views.thirdPartyResult.subTitle.text" tag="p">
        <template #close>
          <el-link
            href="javascript:parent.close();"
            style="font-size: inherit; margin-bottom: 0.05rem">
            <b>{{ i18n.t('views.thirdPartyResult.subTitle.close') }}</b>
          </el-link>
        </template>
      </i18n-t>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'
  import { useRoute } from 'vue-router'

  import { BadgeCheck } from 'lucide-vue-next'
  import { DataSourceAuthorizationService } from '@/client'
  // Store and Route
  const route = useRoute()
  const i18n = useI18n()
  // Data related to the tool and the query
  const toolName: Ref<string> = ref('')
  // variables to check if the request url contains valid query parameters,
  // check if the request is resolved, and if the api Call succeeds
  const validQueryParams: Ref<boolean> = ref(false)
  const apiSuccess: Ref<boolean> = ref(false)
  const waitingForResponse: Ref<boolean> = ref(true)
  /**
   * Sends a request to the api to register the integrations
   */
  async function registerOAuth(state: string, code: string) {
    waitingForResponse.value = true

    DataSourceAuthorizationService.oauthCallbackApiV1DataSourceOauthCallbackPost(
      { requestBody: { state, code } }
    )
      .then(() => {
        // set apiSuccess to true, to render a success message
        apiSuccess.value = true
        // Close Tab after x seconds
        setTimeout(function () {
          parent.close()
        }, 6000)
      })
      .catch((error) => {
        console.error('error', error)
        // set apiSuccess to false, to render a error message
        // do something with the error status and message, when implemented in backend
        apiSuccess.value = false
      })
      .finally(() => {
        // set waitingForResponse to false, to render the template
        waitingForResponse.value = false
      })
  }

  onMounted(async () => {
    const params = route.query
    const state = params.state as string
    const code = params.code as string

    if (params.state) {
      validQueryParams.value = true
    }

    // still call registerOAuth even if the queryParams are not valid, to let the backend know that there was an error
    registerOAuth(state, code)
  })
</script>

<style scoped>
  .highlight {
    color: var(--el-color-primary);
  }
  .error {
    color: var(--el-color-error);
  }
  .vertical-centered {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-content: center;
  }
  * {
    text-align: center;
  }
  .row {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-content: center;
  }
  .x {
    font-size: 2.5rem;
    margin: 0;
    vertical-align: middle;
  }
  .x-wrapper {
    margin-left: 1rem;
    margin-right: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--el-text-color-disabled);
  }
</style>
